import type { IAuthData, IBiometryOptions, ICaller } from '@/interfaces/general'
import { TypeLang } from '@/store/language/language'
import globalEvents from '@/services/GlobalEvents/GlobalEvents'
import { Events } from '@/services/GlobalEvents/constants'
import { Theme } from '@/store/settings/constants'

export const isApplication = () => {
  return !!window.webkit || !!window.Android
}

export const isIOSApp = () => {
  return !!window.webkit
}

// Opens a link in the device's external browser (mobile app only). On web there
// is no native handler, so this is a no-op — handle web navigation separately.
export const openExternalLink = (link: string): boolean => {
  if (!isApplication()) {
    return false
  }
  if (window.webkit?.messageHandlers?.openExternalLink) {
    window.webkit.messageHandlers.openExternalLink.postMessage(link)
    return true
  }
  if (window.Android?.openExternalLink) {
    window.Android.openExternalLink(link)
    return true
  }
  return false
}

export const copyTextToClipboard = (text: string) => {
  if (window.Android?.onCopyToClipboard) {
    window.Android?.onCopyToClipboard(text)
  }
  window.navigator.clipboard.writeText(text)
}

export const copyFileToClipboard = async(filePromise: Promise<Blob>) => {
  if (window.Android) {
    if (!window.Android.onClipboardFileUploaded) {
      return
    }
    const data = new FormData()
    const file = await filePromise
    data.append('file', file)
    const request = new Request('http://127.0.0.1:8080/api/copy-clipboard', {
      method: 'POST',
      body: data
    })
    const { url }: { url: string } = await fetch(request).then(res => res.json())
    window.Android.onClipboardFileUploaded(url)
    return
  }
  try {
    navigator.clipboard.write([
      new ClipboardItem({
        'image/png': filePromise
      })
    ])
  } catch (e) {
    const file = await filePromise
    navigator.clipboard.write([
      new ClipboardItem({
        'image/png': file
      })
    ])
  }

}

export const callToApp = (callers: ICaller) => {
  if (!isApplication()) {
    return
  }
  if (window.webkit?.messageHandlers) {
    window.webkit.messageHandlers.call?.postMessage(callers)
  } else if (window.Android) {
    window.Android.call?.(JSON.stringify(callers))
  }
}

export const sendTokenToApp = (token: string) => {
  if (!isApplication()) {
    return
  }
  if (window.webkit?.messageHandlers) {
    window.webkit.messageHandlers.authToken?.postMessage(token)
  } else if (window.Android) {
    window.Android.authToken?.(token)
  }
}

export const sendAccessTokenToApp = (data: IAuthData) => {
  if (!isApplication()) {
    return
  }
  if (window.webkit?.messageHandlers) {
    window.webkit.messageHandlers.saveAccessToken?.postMessage(data)
  } else if (window.Android) {
    window.Android.saveAccessToken?.(JSON.stringify(data))
  }
}

export const removeAccessTokenFromApp = () => {
  if (!isApplication()) {
    return
  }
  if (window.webkit?.messageHandlers) {
    window.webkit.messageHandlers.removeAccessToken?.postMessage('')
  } else if (window.Android) {
    window.Android.removeAccessToken?.('')
  }
}

export const getAppVersion = async(): Promise<string> => {
  if (window.Android) {
    if (window.Android.getAppVersion) {
      return (window.Android.getAppVersion())
    } else {
      return ('android:2.0.4')
    }
  }

  if (window.webkit) {
    if (window.webkit.messageHandlers?.versionHandler) {
      try {
        return await window.webkit.messageHandlers.versionHandler.postMessage('')
      } catch (e) {
        return 'ios:error'
      }
    } else {
      return 'ios:1.0.9'
    }
  }
  return ('web')
}

export const sendLanguageToApp = (lang: TypeLang) => {
  if (!isApplication()) {
    return
  }
  if (window.webkit?.messageHandlers?.localeHandler) {
    window.webkit.messageHandlers.localeHandler.postMessage(lang)
  } else if (window.Android?.setLanguage) {
    window.Android.setLanguage(lang)
  }
}

export const reloadPageOnIOS = (): boolean => {
  if (!isApplication() || !window.webkit?.messageHandlers?.reload) {
    return false
  }
  window.webkit.messageHandlers.reload.postMessage('')
  return true
}

export const emitReboot = () => {
  if (isApplication()) {
    if (window.webkit?.messageHandlers?.reboot) {
      window.webkit.messageHandlers.reboot.postMessage('')
      return
    } else if (window.Android?.reboot) {
      window.Android.reboot()
      return
    } else if (window.webkit && !window.webkit.messageHandlers?.reload) {
      return
    }
  }
  globalEvents.emit(Events.reboot)
}

/**
 * Уведомление iOS об отправке сообщения для формирования
 * списка активных пользователей
 * @param message
 * @param message.id uid пользователя
 * @param message.label имя пользователя
 * @param message.avatar ссылка на аватар пользователя
 */
export const messageSent = (message: {
  id: string
  label: string
  avatar?: string
}) => {
  if (!isApplication()) {
    return
  }
  const appMessage = {
    ...message,
    avatar: message.avatar || null,
    capability: null
  }
  if (window.webkit?.messageHandlers?.messageSent) {
    window.webkit.messageHandlers.messageSent.postMessage(appMessage)
  } else if (window.Android?.messageSent) {
    window.Android.messageSent(JSON.stringify(appMessage))
  }
}

export const updateUserData = (userData: {
  id: string
  label: string
}) => {
  if (!isApplication()) {
    return
  }
  if (!window.webkit?.messageHandlers?.userData) {
    return
  }
  window.webkit.messageHandlers.userData.postMessage(userData)
}

export const versionToNumber = (version: string) => {
  const versionFormat = version.replace(/^\D+/i, '')
  const versions = versionFormat.split('.').map(value => +value || 0)
  return versions.reduce((sum, value) => sum * 1000 + value, 0)
}

export const notifyThemeChange = (theme: Theme) => {
  if (!isApplication()) {
    return
  }
  if (window.Android?.setTheme) {
    window.Android.setTheme(theme)
  } else if (window.webkit?.messageHandlers?.setTheme) {
    window.webkit.messageHandlers.setTheme.postMessage(theme)
  }
}

export const notifyBiometry = (options: IBiometryOptions): boolean => {
  if (!isApplication()) {
    return false
  }
  if (window.Android?.setBiometry) {
    window.Android.setBiometry(JSON.stringify(options))
    return true
  }
  if (window.webkit?.messageHandlers?.setBiometry) {
    window.webkit.messageHandlers.setBiometry.postMessage(options)
    return true
  }
  return false
}
